import { type ReactNode } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';

import { Chip } from '../primitives/Chip';
import type { UiTheme } from '../theme';

// Single horizontal-scroll chip row with an axis label above it. Used
// by ExercisePicker, ExerciseSwapPicker and CatalogScreen for region +
// equipment filters. Multiple chips on the same row are OR-within-axis;
// stacking two rows of this component is AND-across-axes — the filter
// logic lives in the parent, this is purely presentation.
//
// Compared to the inline version that lived in ExercisePicker pre-
// 1.23.1: the axis label makes it discoverable that the row is a
// filter (instead of looking like a horizontal nav), and the chips
// have more vertical padding so multi-select is comfortable on
// touch.

type Props<T extends string> = {
  theme: UiTheme;
  label: string;
  options: ReadonlyArray<T>;
  active: ReadonlySet<T>;
  onToggle: (value: T) => void;
  renderLabel: (value: T) => string;
  /** Drop the built-in horizontal padding — for hosts whose container
   *  already owns the edge inset (e.g. the Create-hub body). */
  flush?: boolean;
};

export type FilterChipsRowProps<T extends string> = Props<T>;

export function FilterChipsRow<T extends string>({
  theme,
  label,
  options,
  active,
  onToggle,
  renderLabel,
  flush = false,
}: Props<T>): ReactNode {
  const styles = makeStyles(theme);
  return (
    <View style={styles.wrap}>
      <Text style={[styles.label, flush && styles.flush]}>{label}</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[styles.row, flush && styles.flush]}
        keyboardShouldPersistTaps="handled"
      >
        {options.map((opt) => (
          <Chip
            key={opt}
            theme={theme}
            label={renderLabel(opt)}
            selected={active.has(opt)}
            onPress={() => onToggle(opt)}
          />
        ))}
      </ScrollView>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    wrap: {
      paddingTop: s.s2,
      gap: s.s2,
    },
    label: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      paddingHorizontal: s.s5,
    },
    row: {
      flexDirection: 'row',
      gap: s.s2,
      paddingHorizontal: s.s5,
      paddingVertical: s.s1,
    },
    flush: { paddingHorizontal: 0 },
  });
}
